import React, { useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { DatePickerInput } from "react-native-paper-dates";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { ActivityState } from "../../../graphql/generated/graphql";

interface BulkAssessmentModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: {
    numberOfQuestions: number;
    timeLimit: number;
    activityState: ActivityState;
    scheduledDate?: string | Date;
  }) => void;
  isProcessing?: boolean;
}

const DEFAULT_NUMBER_OF_QUESTIONS = 10;
const DEFAULT_TIME_LIMIT = 25;
const MAX_NUMBER_OF_QUESTIONS = 25;
const MAX_TIME_LIMIT = 30;

const BulkAssessmentModal: React.FC<BulkAssessmentModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isProcessing = false,
}) => {
  const { isMedium } = useResponsiveScreen();
  const [validationError, setValidationError] = useState("");

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      numberOfQuestions: DEFAULT_NUMBER_OF_QUESTIONS,
      timeLimit: DEFAULT_TIME_LIMIT,
      activityState: ActivityState.Draft,
      scheduledDate: undefined,
    },
  });

  const watchActivityState = watch("activityState");

  const onFormSubmit = (data) => {
    setValidationError("");
    const formData = {
      numberOfQuestions: data.numberOfQuestions || DEFAULT_NUMBER_OF_QUESTIONS,
      timeLimit: data.timeLimit || DEFAULT_TIME_LIMIT,
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };
    onSubmit(formData);
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={handleClose}
      title="Create Final Assessments"
      style={{ width: isMedium ? "60%" : "90%", maxWidth: 700 }}
      ariaLabel="Bulk Assessment Creation Modal">
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        <View style={styles.container}>
          <CustomText
            text="Generate comprehensive final assessments for all courses in your school"
            size="m"
            weight="medium"
            style={styles.description}
            useTranslationText={false}
          />

          <CustomText
            text="This will create a 'Final Assessment' module for each eligible course, combining questions from all interactive learning experiences across the entire course."
            size="s"
            weight="regular"
            style={styles.subDescription}
            useTranslationText={false}
          />

          <CustomText
            text="Courses without modules, interactive learning experiences, or existing final assessments will be automatically skipped."
            size="s"
            weight="regular"
            style={styles.subDescription}
            useTranslationText={false}
          />

          <View style={styles.formContainer}>
            <CustomText text="Number of Questions" size="m" weight="ultraBold" useTranslationText={false} />
            <CustomText
              style={styles.subText}
              text={`Maximum ${MAX_NUMBER_OF_QUESTIONS} questions per assessment`}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              name="numberOfQuestions"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <CustomTextInput
                  placeholder="e.g. 10"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    if (text === "") {
                      onChange(undefined);
                    } else {
                      const num = parseInt(text, 10);
                      if (!isNaN(num) && num >= 1 && num <= MAX_NUMBER_OF_QUESTIONS) {
                        onChange(num);
                      }
                    }
                  }}
                  value={value?.toString()}
                  style={styles.textInput}
                  keyboardType="numeric"
                  maxLength={3}
                  editable={!isProcessing}
                />
              )}
            />

            <CustomSpacing type="vertical" size="xl" />
            <CustomText text="Time Limit (Optional)" size="m" weight="ultraBold" useTranslationText={false} />
            <CustomText
              style={styles.subText}
              text={`Maximum ${MAX_TIME_LIMIT} minutes per assessment`}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              name="timeLimit"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <CustomTextInput
                  placeholder="e.g. 25"
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    if (text === "") {
                      onChange(undefined);
                    } else {
                      const num = parseInt(text, 10);
                      if (!isNaN(num) && num >= 1 && num <= MAX_TIME_LIMIT) {
                        onChange(num);
                      }
                    }
                  }}
                  value={value?.toString()}
                  style={styles.textInput}
                  keyboardType="numeric"
                  maxLength={2}
                  editable={!isProcessing}
                />
              )}
            />

            <CustomSpacing type="vertical" size="xl" />
            <View style={styles.activityState}>
              <CustomText text="Publish Assessments" size="l" weight="ultraBold" useTranslationText={false} />
              <CustomText
                text="Publish assessments immediately or schedule them for later."
                size="s"
                useTranslationText={false}
                style={styles.stepDescription}
              />
              <Controller
                control={control}
                name="activityState"
                render={({ field: { onChange, value } }) => (
                  <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                    {Object.keys(ActivityState).map((state) => (
                      <CustomButton
                        key={state}
                        text={ActivityState[state]}
                        size="s"
                        style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                        onPress={() => {
                          onChange(ActivityState[state]);
                        }}
                        disabled={isProcessing}
                      />
                    ))}
                  </div>
                )}
              />
              {watchActivityState === ActivityState.Scheduled && (
                <>
                  <CustomText
                    text="Assessments will be automatically published after:"
                    size="s"
                    useTranslationText={false}
                    style={styles.stepDescription}
                  />
                  <Controller
                    control={control}
                    name="scheduledDate"
                    render={({ field: { onChange, value } }) => (
                      <div style={{ maxWidth: 250, marginTop: 10 }}>
                        <DatePickerInput
                          locale="en"
                          value={value instanceof Date ? value : value ? new Date(value) : undefined}
                          onChange={onChange}
                          inputMode="start"
                          mode="outlined"
                          outlineColor={"#D1D5DB"}
                          selectionColor={undefined}
                          activeOutlineColor={"blue"}
                          withDateFormatInLabel={false}
                          placeholder="MM/DD/YYYY"
                          theme={{
                            colors: {
                              primary: "#000000",
                              onSurface: "#667085",
                            },
                            roundness: 8,
                          }}
                          style={styles.datePicker}
                          aria-label="Schedule published date picker"
                          disabled={isProcessing}
                        />
                      </div>
                    )}
                  />
                </>
              )}
            </View>
          </View>

          {validationError && (
            <>
              <CustomSpacing type="vertical" size="s" />
              <CustomText text={validationError} size="s" style={styles.errorText} useTranslationText={false} />
            </>
          )}

          <CustomSpacing type="vertical" size="xl" />

          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              styleType="secondary"
              onPress={handleClose}
              disabled={isProcessing}
              style={styles.cancelButton}
            />
            <CustomButton
              text={isProcessing ? "Creating Assessments..." : "Create Assessments"}
              styleType="primary"
              onPress={handleSubmit(onFormSubmit)}
              disabled={isProcessing}
              style={styles.submitButton}
            />
          </View>
        </View>
      </ScrollView>
    </CustomAdminModal>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    maxHeight: 600,
  },
  container: {
    padding: 20,
  },
  description: {
    color: "#667085",
    marginBottom: 16,
  },
  subDescription: {
    color: "#667085",
    marginBottom: 8,
  },
  formContainer: {
    marginBottom: 16,
  },
  textInput: {
    width: "100%",
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  errorText: {
    color: "#E53E3E",
    fontSize: 14,
    fontWeight: "400",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "rgba(5, 6, 15, 0.10)",
  },
  submitButton: {
    flex: 1,
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
});

export default BulkAssessmentModal;
